"use no memo";

import React, { useEffect } from 'react';

import { BASE_SUPPORT_URL } from 'helpers/paths';
import { logAnalyticsEvent, ANALYTICS_EVENT_NAME } from 'helpers/loggers';

import { Outlink } from '@local/outlink';

import styles from './styles.module.scss';

type BrowserUnsupportedPageProps = {
  hqId?: string;
  isUnableToLoadSite?: boolean;
  isEmbedded?: boolean;
  isMpRedirect?: boolean;
  restoId?: string;
};

export const BrowserUnsupportedPage: React.FunctionComponent<
  BrowserUnsupportedPageProps
> = ({
  hqId = '',
  isUnableToLoadSite = false,
  isEmbedded = false,
  isMpRedirect = false,
  restoId = '',
}) => {
  useEffect(() => {
    if (isMpRedirect) {
      logAnalyticsEvent({
        eventName: ANALYTICS_EVENT_NAME.viewOpenTabPage,
        attributes: {
          restaurant_brand_id: hqId,
          restaurant_location_id: restoId,
          reason: 'direct_to_marketplace_redirect',
        },
      });
    }

    if (isUnableToLoadSite && isEmbedded) {
      logAnalyticsEvent({
        eventName: ANALYTICS_EVENT_NAME.viewOpenTabPage,
        attributes: {
          reason: 'cookies_blocked_iframe',
        },
      });
    }
  }, []);

  function getHeadingText() {
    if (isUnableToLoadSite) {
      return isEmbedded ? `Let's get started!` : 'Hang on one second!';
    }
    return 'Browser not supported.';
  }

  function handleStartOrderClick() {
    if (!isEmbedded) return;
    logAnalyticsEvent({
      eventName: ANALYTICS_EVENT_NAME.selectX,
      attributes: {
        source: 'cookies_blocked_iframe',
        name: 'open_new_tab_button',
      },
    });
  }

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.heading}>{getHeadingText()}</h1>
      <p className={styles.subtext}>
        {isUnableToLoadSite ? (
          <>
            {isEmbedded
              ? `We need to open a new tab to start your order.`
              : `Looks like your browser doesn't support cookies. You'll need to allow cookies from
                 ChowNow so you can log in and place your order.`}
            <Outlink
              ariaLabel={isEmbedded ? 'Start Order' : 'Contact Support'}
              className={styles.btn}
              dataTestId="ConfirmButton"
              to={isEmbedded ? window.location.href : BASE_SUPPORT_URL}
              handleClick={handleStartOrderClick}
            >
              {isEmbedded ? 'Start Order' : 'Contact Support'}
            </Outlink>
          </>
        ) : (
          <>
            It looks like you&apos;re using a web browser that we do not
            actively support. Please use{' '}
            <Outlink
              to="https://www.google.com/chrome/"
              ariaLabel="Google Chrome Link"
              className={styles.link}
            >
              Google Chrome
            </Outlink>{' '}
            or{' '}
            <Outlink
              to="https://www.apple.com/safari/"
              ariaLabel="Safari Link"
              className={styles.link}
            >
              Safari
            </Outlink>{' '}
            to improve your experience.
          </>
        )}
      </p>
    </div>
  );
};
